import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Wallet, LogOut, ShieldCheck, Briefcase, Headset, Database } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Visão Geral", testid: "nav-dashboard-home" },
  { to: "/app/crm", icon: Users, label: "CRM", testid: "nav-dashboard-crm" },
  { to: "/app/atendimentos", icon: Headset, label: "Atendimentos", testid: "nav-dashboard-atendimentos" },
  { to: "/app/financeiro", icon: Wallet, label: "Financeiro", testid: "nav-dashboard-financial" },
  { to: "/app/projetos", icon: Briefcase, label: "Projetos", testid: "nav-dashboard-projects" },
  { to: "/app/cadastros", icon: Database, label: "Cadastros", testid: "nav-dashboard-cadastros" },
  { to: "/app/usuarios", icon: ShieldCheck, label: "Usuários", testid: "nav-dashboard-users", adminOnly: true },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const visibleNav = navItems.filter((i) => !i.adminOnly || user?.role === "admin");

  return (
    <div className="min-h-screen bg-nx-dark text-white font-jakarta flex flex-col md:flex-row overflow-x-hidden" data-testid="dashboard-layout">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-zinc-800 bg-nx-surface min-h-screen sticky top-0">
        <Link to="/" className="px-6 h-16 flex items-center gap-2 border-b border-zinc-800" data-testid="dashboard-logo">
          <img src="/logo-mark.png" alt="NX" className="h-8 w-auto" />
          <span className="font-mono text-[10px] tracking-[0.3em] text-white">COMPANY</span>
          <span className="text-[9px] font-mono font-normal text-zinc-500 tracking-[0.35em] mt-1">OS</span>
        </Link>
        <nav className="flex-1 p-4 space-y-1">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-nx-yellow text-nx-dark font-bold"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 px-2">
            <div className="w-9 h-9 rounded-full bg-nx-yellow/15 border border-nx-yellow/40 flex items-center justify-center font-syne font-bold text-nx-yellow text-sm">
              {(user?.name || "U")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate" data-testid="user-name-display">{user?.name}</p>
              <p className="text-[11px] text-zinc-500 truncate" data-testid="user-email-display">{user?.email}</p>
            </div>
            <button onClick={handleLogout} data-testid="logout-button" title="Sair" className="text-zinc-500 hover:text-red-400 transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Topbar mobile */}
      <div className="md:hidden sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo-mobile">
            <img src="/logo-mark.png" alt="NX" className="h-7 w-auto" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-white">COMPANY</span>
          </Link>
          <div className="flex items-center gap-1">
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`${item.testid}-mobile`}
                className={({ isActive }) =>
                  `p-2.5 rounded-lg transition-colors ${isActive ? "bg-nx-yellow text-nx-dark" : "text-zinc-400"}`
                }
              >
                <item.icon className="w-4 h-4" />
              </NavLink>
            ))}
            <button onClick={handleLogout} data-testid="logout-button-mobile" className="p-2.5 text-zinc-400 hover:text-red-400">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
}
